import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';

const MENU_MAX = 260;

/**
 * Type-to-search dropdown. Behaves like a <select> (the value is always one of
 * `options`, or '' for none) but the closed control is a text box: typing filters
 * the list instead of forcing the user to scroll a long native menu.
 *
 * options: array of strings
 */
function SearchSelect({ value, onChange, options = [], placeholder = 'Search…', emptyLabel = '—', autoFocus = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState(null);   // fixed-position menu box, so table/card scroll containers can't clip it
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const q = query.trim().toLowerCase();
  // While closed (or before the user types) show every option; once typing, filter.
  const matches = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  // index 0 is the "clear" row, so option i sits at highlight index i + 1
  const rowCount = matches.length + 1;

  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) close(); };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open, close]);

  // anchor the menu under (or above) the input, tracking scroll/resize while open
  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 8;
      const up = below < 140 && r.top > below;
      setPos({
        left: r.left,
        width: r.width,
        maxHeight: Math.min(MENU_MAX, Math.max(120, up ? r.top - 8 : below)),
        ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open]);

  // keep the highlighted row in view while arrowing through a long list
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[hi];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);

  const openList = () => {
    if (open) return;
    setOpen(true);
    setQuery('');
    // start on the current value so Enter re-picks it rather than clearing
    const i = options.indexOf(value);
    setHi(i >= 0 ? i + 1 : 0);
  };

  const pick = (v) => { onChange(v); close(); if (inputRef.current) inputRef.current.blur(); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { openList(); return; }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHi((h) => (h + step + rowCount) % rowCount);
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      pick(hi === 0 ? '' : matches[hi - 1]);
    } else if (e.key === 'Escape') {
      if (!open) return;
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab') {
      close();
    }
  };

  return (
    <div className={`ss-wrap ${open ? 'open' : ''}`} ref={wrapRef}>
      <input
        ref={inputRef}
        className={`mk-input ss-input ${!open && !value ? 'ss-none' : ''}`}
        autoFocus={autoFocus}
        value={open ? query : (value || '')}
        placeholder={open ? placeholder : (value ? '' : emptyLabel)}
        onFocus={openList}
        onClick={openList}
        onChange={(e) => { setQuery(e.target.value); setHi(e.target.value.trim() ? 1 : 0); setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      <span className="ss-caret" onMouseDown={(e) => { e.preventDefault(); if (open) close(); else inputRef.current?.focus(); }}>▾</span>
      {open && pos && (
        <div className="ss-menu" ref={listRef} style={pos}>
          <div className={`ss-opt ss-clear ${hi === 0 ? 'hi' : ''}`}
               onMouseEnter={() => setHi(0)} onMouseDown={(e) => { e.preventDefault(); pick(''); }}>
            {emptyLabel}
          </div>
          {matches.map((o, i) => (
            <div key={o} className={`ss-opt ${hi === i + 1 ? 'hi' : ''} ${o === value ? 'sel' : ''}`}
                 onMouseEnter={() => setHi(i + 1)} onMouseDown={(e) => { e.preventDefault(); pick(o); }}>
              {o}
            </div>
          ))}
          {matches.length === 0 && <div className="ss-empty">No match for “{query.trim()}”</div>}
        </div>
      )}
    </div>
  );
}

export default SearchSelect;
